/**
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { lazy, Suspense, useEffect } from "react";

// react-router components
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

// @mui material components
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";

// Material Kit 2 React themes
import theme from "assets/theme";
import Presentation from "layouts/pages/presentation";

// Material Kit 2 React routes
import AdminTablePage from "customComponents/AdminComponents/RoleMapping";
import ConfigWrapper from "customComponents/ConfigWrapper";
import Loader from "customComponents/Loader";
import AdminLayout from "layouts/pages/admin";
import DashboardLayout from "layouts/pages/admin/dashboardLayout";
import AdminSignupPage from "layouts/pages/admin/signup";
import ForgotPasswordPage from "layouts/pages/authentication/forgot-password";
import SignInPage from "layouts/pages/authentication/sign-in";
import SignUpPage from "layouts/pages/authentication/sign-up";
import CookiePolicyPage from "layouts/pages/landing-pages/cookie-policy";
import DataPrivacyPolicyPage from "layouts/pages/landing-pages/data-privacy-policy";
import DataRetentionPolicyPage from "layouts/pages/landing-pages/data-retention-policy";
import DataSubjectRightsPolicyPage from "layouts/pages/landing-pages/data-subject-rights-policy";
import SuperAdminLayout from "layouts/pages/super-admin";
import { b2cTechyrrDomains } from "lib/utilities/globalConstants";
import { defaultToastConfig } from "lib/utilities/taostConfig";
import AdminHome from "pages/Admin/AdminHome";
import AdminPricing from "pages/Admin/Pricing";
import TransactionHistory from "pages/Admin/TransactionHistory";
import NotAuthorized from "pages/NotAuthorized";
import ApproveCustomSkills from "pages/SuperAdmin/ApproveCustomSkills";
import { AuthProvider } from "provider/AuthProvider";
import { BreadcrumbProvider } from "provider/BreadcrumbProvider";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import routes from "webRoutes";
import "./index.css";
import SuperDashboard from "./pages/SuperAdmin/Dashboard/index";
import ProtectedRoute from "./provider/ProtectedRoute";

import FormViewsCandidates from "pages/Admin/CandidatesView";

import { TutorialProvider } from "provider/TutorialProvider";

const CandidatePageV1 = lazy(() => import("pages/Admin/V1/Candidate"));
const AdminDashboardV1 = lazy(() => import("pages/Admin/V1/Dashboard"));
const FormPageV1 = lazy(() => import("pages/Admin/V1/FormPage"));
const CandidatePageV2 = lazy(() => import("pages/Admin/V2/Candidate"));
const AdminDashboardV2 = lazy(() => import("pages/Admin/V2/Dashboard"));
const FormPageV2 = lazy(() => import("pages/Admin/V2/FormPage"));
const CandidatePageV4 = lazy(() => import("pages/Admin/V4/Candidate"));
const AdminDashboardV4 = lazy(() => import("pages/Admin/V4/Dashboard"));
const FormPageV4 = lazy(() => import("pages/Admin/V4/FormPage"));
const CandidatePageV5 = lazy(() => import("pages/Admin/V5/Candidate"));
const AdminDashboardV5 = lazy(() => import("pages/Admin/V5/Dashboard"));
const FormPageV5 = lazy(() => import("pages/Admin/V5/FormPage"));
const UploadResume = lazy(() => import("pages/Admin/V6/UploadResume"));
const FormViews = lazy(() => import("pages/Admin/FormViews"));

export default function App() {
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    b2cTechyrrDomains.includes(window.location.host) &&
      (document.title = `Techyrr - AI-Powered Tech Interviews to Land Your Dream Job`);
  }, []);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.route}
            uniqueName={route.uniqueName || `UNIQUE_NAME_${route.route}`}
          />
        );
      }

      return null;
    });

  return (
    <AuthProvider>
    <TutorialProvider>
    <ThemeProvider theme={theme}>
      <BreadcrumbProvider>
        <ToastContainer {...defaultToastConfig} />
          <CssBaseline />
          <Routes>
            {getRoutes(routes)}
            <Route path="/signin/:origin?" element={<ConfigWrapper Component={<SignInPage />} />} />
            <Route path="/signup/:origin?" element={<ConfigWrapper Component={<SignUpPage />} />} />
            <Route
              path="/forgot-password/:origin?"
              element={<ConfigWrapper Component={<ForgotPasswordPage />} />}
            />
            <Route
              path="/techyrr-admin/signup/:origin?"
              element={<ConfigWrapper Component={<SignUpPage techyrrAdmin={true} />} />}
            />
            <Route
              path="/techyrr-admin/signin/:origin?"
              element={<ConfigWrapper Component={<SignInPage techyrrAdmin={true} />} />}
            />
            <Route
              path="/techyrr-admin/forgot-password/:origin?"
              element={<ConfigWrapper Component={<ForgotPasswordPage techyrrAdmin={true} />} />}
            />
            <Route
              path="/admin/role-mapping"
              element={
                <AdminLayout
                  component={
                    <ProtectedRoute uniqueName="RoleMapping_Page" component={<AdminTablePage />} />
                  }
                />
              }
            />
            <Route
              path="/"
              element={
                b2cTechyrrDomains.includes(window.location.host) ? (
                  <Navigate to="/techyrr/take-test" replace />
                ) : (
                  <Presentation />
                )
              }
            />
            <Route path="/not-found" element={<NotAuthorized />} />
            <Route path="*" element={<Navigate to="/presentation" />} />
            <Route path="/admin/signup/:inviteId" element={<AdminSignupPage />} />
            {/* Super Admin Routes */}
            <Route
              path="/super-admin/dashboard"
              element={<SuperAdminLayout component={<SuperDashboard />} />}
            />
            <Route
              path="/super-admin/approve-custom-skills/:companyId/:formId"
              element={<SuperAdminLayout component={<ApproveCustomSkills />} />}
            />
            {/* Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <AdminLayout
                  component={
                    <ProtectedRoute uniqueName="AdminDashboard_Page" component={<AdminHome />} />
                  }
                />
              }
            />
            <Route
              path="/admin/transactions"
              element={
                <AdminLayout
                  component={
                    <ProtectedRoute
                      uniqueName="TransactionHistory_Page"
                      component={<TransactionHistory />}
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/pricing"
              element={
                <AdminLayout
                  component={
                    <ProtectedRoute uniqueName="AdminPricing_Page" component={<AdminPricing />} />
                  }
                />
              }
            />
            {/* Admin V1 Routes */}
            <Route
              path="/admin/v1/dashboard"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV1Dashboard_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <AdminDashboardV1 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v1/dashboard/form/:formId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV1Form_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <FormPageV1 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v1/dashboard/form/:formId/candidate/:candidateId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV1Candidate_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <CandidatePageV1 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            {/* Admin V2 Routes */}
            <Route
              path="/admin/v2/dashboard"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV2Dashboard_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <AdminDashboardV2 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v2/dashboard/form/:formId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV2Form_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <FormPageV2 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v2/dashboard/form/:formId/candidate/:candidateId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV2Candidate_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <CandidatePageV2 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/:licence/dashboard/form/:formId/view"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="FormViews_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <FormViews />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/:licence/dashboard/form/:formId/view/:viewId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV2Form_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <FormViewsCandidates />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />

            {/* V3 Routes */}
            <Route
              path="/techyrr/take-test"
              element={
                <ProtectedRoute
                  uniqueName="V3UploadResume_Page"
                  component={<AdminLayout component={<UploadResume />} loadFullLayout={false} />}
                />
              }
            />
            {/* Admin V4 Routes */}
            <Route
              path="/admin/v4/dashboard"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV4Dashboard_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <AdminDashboardV4 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v4/dashboard/form/:formId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV4Form_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <FormPageV4 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v4/dashboard/form/:formId/candidate/:candidateId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV4Candidate_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <CandidatePageV4 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            {/* Admin V5 Routes */}
            <Route
              path="/admin/v5/dashboard"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV5Dashboard_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <AdminDashboardV5 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v5/dashboard/form/:formId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV5Form_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <FormPageV5 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route
              path="/admin/v5/dashboard/form/:formId/candidate/:candidateId"
              element={
                <AdminLayout
                  component={
                    <DashboardLayout
                      component={
                        <ProtectedRoute
                          uniqueName="AdminV5Candidate_Page"
                          component={
                            <Suspense fallback={<Loader />}>
                              <CandidatePageV5 />
                            </Suspense>
                          }
                        />
                      }
                    />
                  }
                />
              }
            />
            <Route path="/techyrr/signin" element={<SignInPage techyrrAdmin={true} />} />
            <Route path="/techyrr/signup/" element={<AdminSignupPage techyrrAdmin={true} />} />
            <Route
              path="/techyrr/forgot-password"
              element={<ForgotPasswordPage techyrrAdmin={true} />}
            />
            {/* Policy Pages */}
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/data-privacy-policy" element={<DataPrivacyPolicyPage />} />
            <Route path="/data-retention-policy" element={<DataRetentionPolicyPage />} />
            <Route path="/data-subject-rights-policy" element={<DataSubjectRightsPolicyPage />} />
          </Routes>
      </BreadcrumbProvider>
    </ThemeProvider>
    </TutorialProvider>
    </AuthProvider>
  );
}
